import { useState, useRef, useEffect } from "react";
import { IdentityConsentCard } from "@/components/agent/IdentityConsentCard";
import { BookingCard } from "@/components/agent/BookingCard";
import { AccessCard } from "@/components/agent/AccessCard";
import { gaEvent, gaEventOnce } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => {
  let id = localStorage.getItem("vale_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("vale_session", id);
  }
  return id;
};

const getVisitorId = () =>
  localStorage.getItem("dane_visitor_id") || sessionStorage.getItem("dane_visitor_id") || null;

const stripTags = (t) =>
  t.replace(/\s*\[\[CHIPS:[^\]]*\]\]\s*/gs, " ").replace(/\s*\[\[[^\]]*\]\]\s*/g, " ").trim();
const stripStreaming = (t) => stripTags(t).replace(/\[\[[^\]]*$/s, "").trimEnd();
const renderBold = (text) =>
  text.split("**").map((part, i) =>
    i % 2 === 1 ? <strong key={i} className="font-semibold text-white">{part}</strong> : part
  );

export const ChatPanel = () => {
  const [config, setConfig] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState("");
  const sessionId = useRef(getSessionId());
  const visitorId = useRef(getVisitorId());
  const scrollRef = useRef(null);
  const msgCountRef = useRef(0);
  const identityPendingRef = useRef(false);

  useEffect(() => {
    fetch(`${API}/config?session_id=${sessionId.current}&visitor_id=${visitorId.current || ""}`)
      .then((r) => r.json()).then(setConfig).catch(() => {});
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, streamText]);

  const send = async (text) => {
    if (!text.trim() || streaming) return;
    msgCountRef.current += 1;
    gaEventOnce("chat_start", "chat_start");
    gaEvent("chat_message_sent", { message_count: msgCountRef.current });
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setStreaming(true);
    setStreamText("");
    let acc = "";
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text, visitor_id: visitorId.current }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let actions = [];
      let chips = [];
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const ev = JSON.parse(line.slice(6));
          if (ev.type === "delta") {
            acc += ev.content;
            setStreamText(stripStreaming(acc));
          } else if (ev.type === "done") {
            actions = ev.actions || [];
            chips = ev.chips || [];
          }
        }
      }
      const hasCard = actions.some((a) => ["verify_identity", "request_access", "book_meeting"].includes(a));
      const final = [{ role: "agent", content: stripTags(acc), chips: hasCard ? [] : chips }];
      if (actions.includes("verify_identity") && !identityPendingRef.current) {
        identityPendingRef.current = true;
        final.push({ role: "card", card: "identity" });
      }
      if (actions.includes("request_access")) final.push({ role: "card", card: "access" });
      if (actions.includes("book_meeting")) final.push({ role: "card", card: "booking" });
      if (actions.includes("delete_data")) {
        try {
          const dr = await fetch(`${API}/data-deletion`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ session_id: sessionId.current, visitor_id: visitorId.current }),
          });
          const dd = await dr.json();
          localStorage.removeItem("dane_visitor_id");
          sessionStorage.removeItem("dane_visitor_id");
          localStorage.removeItem("vale_session");
          final.push({ role: "system", content: dd.message || "Your data has been deleted." });
        } catch {
          final.push({ role: "system", content: "The deletion didn't complete — please try again." });
        }
      }
      setMessages((m) => [...m, ...final]);
    } catch {
      setMessages((m) => [...m, { role: "agent", content: "The connection dropped — please try that again." }]);
    }
    setStreamText("");
    setStreaming(false);
  };

  const lastAgentIdx = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "agent") return i;
      if (messages[i].role === "user") return -1;
    }
    return -1;
  })();

  return (
    <div className="border border-[#2E2E2E] bg-[#111111]" data-testid="chat-panel">
      <div ref={scrollRef} className="chat-scroll overflow-y-auto px-6 md:px-8 py-8 space-y-5" style={{ maxHeight: 460, minHeight: 260 }}>
        {messages.length === 0 && config && (
          <div data-testid="chat-greeting">
            <p className="text-[19px] leading-[1.7] text-[#D6D6D6]">{config.greeting}</p>
            {(config.suggested_prompts || []).length > 0 && (
              <div className="mt-6 flex flex-wrap gap-3">
                {config.suggested_prompts.slice(0, 4).map((p) => (
                  <button key={p} onClick={() => send(p)} data-testid="chat-suggested-prompt"
                    className="text-[17px] font-medium text-[#D6D6D6] border border-[#2E2E2E] px-4 py-2.5 hover:border-[#FF5C5C] hover:text-white transition-colors duration-300 text-left">
                    {p}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {messages.map((m, i) => {
          if (m.role === "card") {
            if (m.card === "identity") return (
              <IdentityConsentCard key={i} sessionId={sessionId.current} visitorId={visitorId.current} api={API}
                consentText={config?.consent_text}
                onVerified={() => setMessages((mm) => [...mm, { role: "system", content: "You're verified — thanks. Keep going below." }])} />
            );
            if (m.card === "booking") return <BookingCard key={i} sessionId={sessionId.current} api={API} />;
            if (m.card === "access") return (
              <AccessCard key={i} sessionId={sessionId.current} visitorId={visitorId.current} api={API}
                onInside={() => window.location.assign("/talk")} />
            );
            return null;
          }
          if (m.role === "system") return (
            <p key={i} className="text-[17px] text-[#D6D6D6] border-l border-[#2E2E2E] pl-4" data-testid="chat-message-system">{m.content}</p>
          );
          const isUser = m.role === "user";
          return (
            <div key={i}>
              <div data-testid={isUser ? "chat-message-user" : "chat-message-agent"}
                className={`max-w-[85%] border border-[#2E2E2E] px-5 py-4 text-[17px] leading-[1.7] ${isUser ? "ml-auto bg-[#050505] text-white" : "bg-[#111111] text-[#D6D6D6]"}`}>
                {renderBold(m.content)}
              </div>
              {!isUser && i === lastAgentIdx && !streaming && (m.chips || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2.5">
                  {m.chips.map((c) => (
                    <button key={c} onClick={() => send(c)} data-testid="chat-chip"
                      className="text-[17px] font-medium text-[#FF5C5C] border border-[#2E2E2E] px-3.5 py-2 hover:border-[#FF5C5C] transition-colors duration-300">
                      {c}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {streaming && (
          <div className="max-w-[85%] border border-[#2E2E2E] bg-[#111111] px-5 py-4 text-[17px] leading-[1.7] text-[#D6D6D6]" data-testid="chat-streaming">
            {streamText || "…"}
          </div>
        )}
      </div>
      <form
        onSubmit={(e) => { e.preventDefault(); send(input); }}
        className="flex items-center gap-3 border-t border-[#2E2E2E] px-4 md:px-6 py-4">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Tell me what you're working with."
          data-testid="chat-input"
          className="flex-1 bg-transparent text-[17px] font-medium text-white placeholder:text-[#D6D6D6] outline-none py-2"
        />
        <button type="submit" disabled={streaming || !input.trim()} data-testid="chat-send-button"
          className="bg-[#FFFFFF] text-[#050505] text-[17px] font-medium px-6 py-2.5 min-h-[44px] disabled:opacity-40 hover:bg-[#D6D6D6] transition-colors duration-300">
          Send
        </button>
      </form>
    </div>
  );
};
